import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Palette,
  Table2,
  Link2,
  FileText,
  BarChart3,
  Database,
  Code2,
  Zap,
  ArrowRight,
  ChevronRight,
  Play,
  Pause,
  LogIn,
  KeyRound,
  ExternalLink,
  Layers,
  Image,
  Send,
  Search,
  FileCheck,
  PenTool,
  Users,
  Globe,
  Clock,
  ShieldCheck,
  LayoutGrid,
  Mail,
  CheckCircle2,
  Star,
  Sparkles,
  Wand2,
  Key,
  FileImage,
  Download,
  Upload,
  ThumbsUp,
  Eye,
  Share2,
  Target,
  TrendingUp,
  Rocket,
  Heart,
  CalendarDays,
  Paintbrush,
  UserPlus,
  Building2,
  Globe2,
  Sheet,
  Link as LinkIcon,
  Workflow,
  LayoutDashboard,
  Check,
  CreditCard,
} from "lucide-react";
import "./LandingPage.css";
import LandingDemo from "./LandingDemo";

/* ─────────────── data ─────────────── */

const FEATURES = [
  {
    icon: Palette,
    title: "Template Editor",
    desc: "Design pixel-perfect templates with text, images, shapes, QR codes, and star ratings. Full creative control with layer management, alignment tools, and real-time preview.",
    highlights: ["15+ shape tools", "QR code generation", "Dynamic star ratings"],
  },
  {
    icon: Table2,
    title: "SnapSheet",
    desc: "Spreadsheet interface for bulk data management. Import CSV data, map columns to template variables, and generate thousands of personalized variations in seconds.",
    highlights: ["CSV import/export", "Per-row overrides", "Image columns"],
  },
  {
    icon: Link2,
    title: "SnapLinks",
    desc: "Generate unique personalized URLs for every row in your data. Each link renders a custom image with the recipient\u2019s data automatically embedded.",
    highlights: ["Unique URLs per row", "Auto-slug generation", "Click tracking"],
  },
  {
    icon: FileText,
    title: "SnapDocs",
    desc: "Create personalized PDFs with e-signature workflows. Route contracts through multi-party signing with verification metadata and consent capture.",
    highlights: ["Multi-party signing", "Audit trails", "Review & sign modes"],
  },
  {
    icon: BarChart3,
    title: "SnapReports",
    desc: "Generate branded, data-driven PDF reports from live data sources. Schedule automated weekly or monthly delivery to clients with one click.",
    highlights: ["Scheduled delivery", "6+ report templates", "Live data binding"],
  },
  {
    icon: Database,
    title: "Data Connectors",
    desc: "Pull real-time metrics from Google Analytics, Search Console, DataForSEO, and more. Power your reports with live data from 10+ integrations.",
    highlights: ["GA4 & GSC", "DataForSEO (11 endpoints)", "Auto-refresh"],
  },
];

const WORKFLOW_STEPS = [
  {
    icon: PenTool,
    title: "Design Your Templates",
    desc: "Use the visual editor to create branded images, reports, proposals, and contracts. Add dynamic variables like {{first_name}} or {{company}} that auto-fill from data.",
  },
  {
    icon: Table2,
    title: "Import Prospect Data",
    desc: "Upload a CSV of prospects to SnapSheet, or connect data enrichment tools like TexAu and PhantomBuster to auto-populate contact info and company details.",
  },
  {
    icon: Image,
    title: "Generate Personalized Images at Scale",
    desc: "SnapLayer renders a unique image for every row \u2014 each with the prospect\u2019s name, logo, and data baked in. Export as a ZIP or use SnapLinks for dynamic delivery.",
  },
  {
    icon: Send,
    title: "Send Personalized Outreach",
    desc: "Email prospects with their personalized preview images and SnapLinks. When they click, they see a custom landing experience built just for them.",
  },
  {
    icon: Search,
    title: "Trigger Data Connectors for Full Reports",
    desc: "When a prospect engages, DataForSEO, GA4, and Google Search Console connectors pull real data \u2014 keyword rankings, traffic metrics, backlink profiles \u2014 into SnapLayer automatically.",
  },
  {
    icon: BarChart3,
    title: "Auto-Deliver Branded Reports",
    desc: "SnapReports compiles the data into a polished multi-page PDF with your agency branding. Schedule weekly or monthly delivery so clients always receive fresh insights.",
  },
  {
    icon: FileCheck,
    title: "Close Deals with SnapDocs",
    desc: "Send a proposal and contract via SnapDocs. Multi-party e-signature routing means the client reviews, signs, and your agency closes the deal \u2014 all from one platform.",
  },
];

const CONNECTORS = [
  { name: "Google Analytics 4", desc: "Traffic & conversions", available: true },
  { name: "Google Search Console", desc: "SEO rankings & clicks", available: true },
  { name: "DataForSEO", desc: "11 keyword & backlink APIs", available: true },
  { name: "Google Business Profile", desc: "Reviews & ratings", available: false },
  { name: "BrightLocal", desc: "Citation management", available: false },
  { name: "Zapier", desc: "6,000+ app automations", available: false },
  { name: "Make (Integromat)", desc: "Visual automation flows", available: false },
  { name: "Pabbly Connect", desc: "Affordable automation", available: false },
  { name: "TexAu", desc: "LinkedIn data enrichment", available: false },
  { name: "PhantomBuster", desc: "Social data extraction", available: false },
];

/* ── AI Features ── */
const AI_FEATURES = [
  {
    icon: Wand2,
    title: "AI Image Generation",
    desc: "Generate unique images directly inside the editor. Powered by DALL·E 3 and Stable Diffusion via Straico.",
    providers: ["DALL·E 3", "Stable Diffusion"],
  },
  {
    icon: Sparkles,
    title: "AI Text Enhancement",
    desc: "Refine headlines, CTAs, and body copy with GPT-4o-mini. Rewrite, expand, or condense any text layer in one click.",
    providers: ["GPT-4o-mini"],
  },
  {
    icon: Key,
    title: "Bring Your Own Key (BYOK)",
    desc: "Use your own API keys from Straico, OpenAI, or Qolaba. No middleman markup — pay provider rates directly.",
    providers: ["Straico", "OpenAI", "Qolaba"],
  },
];

/* ── Use Cases ── */
const USE_CASES = [
  {
    icon: Send,
    title: "Personalized Cold Outreach",
    desc: "Create unique prospect images with names, logos, and data baked in. Generate SnapLinks for 1-to-1 landing pages. Track opens and clicks.",
    tags: ["Images", "SnapLinks", "Tracking"],
  },
  {
    icon: BarChart3,
    title: "Automated Client Reporting",
    desc: "Pull live data from GA4, Search Console, and DataForSEO. Generate branded multi-page PDFs. Schedule weekly or monthly delivery — automatically.",
    tags: ["Reports", "Data Connectors", "Scheduling"],
  },
  {
    icon: FileCheck,
    title: "Proposals & Contract Signing",
    desc: "Build personalized proposals and contracts with e-signature workflows, multi-party routing, and evidence-focused audit trails.",
    tags: ["SnapDocs", "E-Signatures", "Audit Trail"],
  },
  {
    icon: Image,
    title: "Social Media Content at Scale",
    desc: "Design social media templates then generate hundreds of variations from a spreadsheet. Perfect for franchises, agencies, and multi-location brands.",
    tags: ["Social Media", "Bulk Generation", "Templates"],
  },
  {
    icon: Star,
    title: "Certificates & Achievement Badges",
    desc: "Generate personalized course certificates, achievement badges, and training completion cards — each with the recipient's name, date, and credentials.",
    tags: ["Certificates", "Training", "Badges"],
  },
  {
    icon: Mail,
    title: "Chatbot & Email Auto-Replies",
    desc: "Generate personalized images on-the-fly for chatbot responses, automated email sequences, and text message campaigns via the API.",
    tags: ["Chatbots", "Email", "API"],
  },
  {
    icon: CalendarDays,
    title: "Event Tickets & Invitations",
    desc: "Create branded event tickets, conference badges, and personalized invitations complete with QR codes and attendee details.",
    tags: ["Events", "QR Codes", "Personalization"],
  },
  {
    icon: Target,
    title: "Agency Sales Enablement",
    desc: "Personalize demo decks, capability overviews, and case studies for each prospect. Every touchpoint becomes a 1-to-1 experience.",
    tags: ["Sales", "Personalization", "PDFs"],
  },
  {
    icon: Globe,
    title: "Real Estate & Product Listings",
    desc: "Generate personalized property flyers, product mockups, and branded listing sheets from spreadsheet data. Scale across hundreds of SKUs or properties.",
    tags: ["Real Estate", "Products", "Bulk"],
  },
];

/* ── Roadmap ── */
const SHIPPED_FEATURES = [
  { icon: LinkIcon, title: "SnapLinks", desc: "Personalized landing pages from query-string variables" },
  { icon: LayoutDashboard, title: "SnapPages", desc: "Public personalized landing pages from templates" },
  { icon: Code2, title: "Developer API", desc: "40+ REST endpoints for rendering, PDFs, templates, and automation" },
  { icon: FileImage, title: "PSD Import & Export", desc: "Import Photoshop files, preserve layers, export back to PSD" },
];

const ROADMAP_ITEMS = [
  { icon: Sheet, title: "Google Sheets", desc: "Two-way sync between SnapSheet and Google Sheets", status: "coming-soon" },
  { icon: UserPlus, title: "Team Members", desc: "Invite team members with role-based permissions", status: "q1-2026" },
  { icon: Building2, title: "Sub Accounts", desc: "Manage client workspaces from one agency dashboard", status: "q1-2026" },
  { icon: Paintbrush, title: "White-Label", desc: "Your brand, your domain — fully custom for clients", status: "q1-2026" },
  { icon: Globe2, title: "Custom Domains", desc: "Serve SnapLinks and pages on your own domain", status: "q1-2026" },
  { icon: Workflow, title: "Zapier / Make / Pabbly", desc: "Native triggers and actions for automation platforms", status: "q2-2026" },
];

/* ── Pricing Plans ── */
const PRICING_PLANS = [
  {
    name: "Solo",
    price: 29,
    annual: 23,
    desc: "For freelancers and solo consultants",
    features: [
      "10 templates",
      "1 GB storage",
      "50 SnapSheet rows",
      "5 text fields / template",
      "3 image fields / template",
      "Community access (limited)",
    ],
    highlight: false,
  },
  {
    name: "Plus",
    price: 79,
    annual: 63,
    desc: "For growing teams and small agencies",
    features: [
      "25 templates",
      "5 GB storage",
      "10 SnapLinks",
      "100 SnapSheet rows",
      "3 SnapPages",
      "PSD import & export",
      "Enhanced community",
    ],
    highlight: false,
  },
  {
    name: "Pro",
    price: 149,
    annual: 119,
    desc: "For established agencies",
    features: [
      "50 templates",
      "20 GB storage",
      "25 SnapLinks",
      "250 SnapSheet rows",
      "10 SnapPages",
      "3 team members",
      "25 sub accounts",
      "PSD import & export",
      "Full community access",
    ],
    highlight: true,
  },
  {
    name: "Agency",
    price: 249,
    annual: 199,
    desc: "For agencies at scale",
    features: [
      "100 templates",
      "100 GB storage",
      "50 SnapLinks",
      "1,000 SnapSheet rows",
      "25 SnapPages",
      "10 team members",
      "50 sub accounts",
      "50 text / 30 image fields",
      "Full community access",
    ],
    highlight: false,
  },
];

/* ── Smoke particle generation helper (144 particles like GSL) ── */
function generateSmokeParticles(count: number) {
  const particles: React.CSSProperties[] = [];
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const dist = 20 + Math.random() * 60;
    particles.push({
      left: `${42 + Math.random() * 16}%`,
      top: `${54 + Math.random() * 8}%`,
      animationDelay: `${(i * 2.6) / count}s`,
      "--dx": `${Math.cos(angle) * dist}px`,
      "--dy": `${-10 - Math.random() * 40}px`,
      "--sm": `${0.6 + Math.random() * 0.5}`,
      "--sl": `${0.9 + Math.random() * 0.5}`,
      "--se": `${1.0 + Math.random() * 0.6}`,
      width: `${6 + Math.random() * 10}px`,
      height: `${6 + Math.random() * 10}px`,
    } as React.CSSProperties);
  }
  return particles;
}

const SMOKE_PARTICLES = generateSmokeParticles(144);

/* ── Rocket component with blast-off ── */
const RocketAnimation: React.FC = () => {
  const [launched, setLaunched] = useState(false);
  const flameSlots = ["fl-left", "fl-center", "fl-right", "fl-left-2", "fl-center-2", "fl-right-2"];

  useEffect(() => {
    // Auto-launch after 4s on screen
    const timer = setTimeout(() => setLaunched(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`rocket-container ${launched ? "rocket-blastoff" : "rocket-float"}`}
      onClick={() => !launched && setLaunched(true)}
    >
      <div className="relative w-full h-full">
        {/* Tower/Pad (back layer) */}
        <img src="/assets/rocket/TowerPad_512.png" alt="" className="rocket-layer rocket-layer-tower" />
        {/* Flame image */}
        <img src="/assets/rocket/Flame_512.png" alt="" className={`rocket-layer rocket-layer-flame-img ${launched ? "rocket-flame-intense" : ""}`} />
        {/* CSS animated flames */}
        {flameSlots.map((slot) => (
          <div key={slot} className={`rocket-flame-slot ${slot} ${launched ? "rocket-flame-intense" : ""}`}>
            <div className="rocket-flame-core" />
            <div className="rocket-flame-glow" />
          </div>
        ))}
        {/* Smoke image */}
        <img src="/assets/rocket/Smoke_512.png" alt="" className="rocket-layer rocket-layer-smoke-img" style={{ zIndex: 30 }} />
        {/* Smoke particles — all 144 */}
        <div className={`rocket-smoke-zone ${launched ? "rocket-smoke-intense" : ""}`}>
          {SMOKE_PARTICLES.map((style, i) => (
            <div key={i} className="rocket-smoke-dot" style={style} />
          ))}
        </div>
        {/* Rocket body (top) */}
        <img src="/assets/rocket/Rocket_512.png" alt="SnapLayer Rocket" className="rocket-layer rocket-layer-rocket-img" style={{ zIndex: 40 }} />
      </div>
    </div>
  );
};

/* ─────────────── component ─────────────── */

const LandingPage: React.FC = () => {
  const [annualBilling, setAnnualBilling] = useState(true);

  return (
    <main className="min-h-screen bg-sl-bg text-white">
      {/* ─── HERO ─── */}
      <section className="landing-hero">
        <div className="relative z-10 container mx-auto px-6 py-20 md:py-28">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-10">
            {/* Left: text content */}
            <div className="flex-1 flex flex-col items-center md:items-start text-center md:text-left gap-6">
              <img
                src="/brand/snaplayer-wordmark-transparent.png"
                alt="SnapLayer"
                className="h-14 md:h-18 lg:h-20 object-contain drop-shadow"
              />

              <span className="landing-badge">Agency Automation Platform</span>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
                Turn Templates Into{" "}
                <span className="gradient-text">Revenue</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
                Design personalized images, reports, proposals, and contracts.
                Automate delivery at scale. Close deals faster with data-driven
                outreach &mdash; all from one platform.
              </p>

              <div className="flex flex-wrap gap-4 mt-2">
                <a
                  href="#demo"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-sl-primary to-sl-accent hover:opacity-90 transition-opacity"
                >
                  <Play className="w-4 h-4" /> Watch Demo
                </a>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-slate-200 border border-slate-600 hover:border-slate-400 transition-colors"
                >
                  <LogIn className="w-4 h-4" /> Log In
                </Link>
              </div>
            </div>

            {/* Right: Rocket animation */}
            <div className="hidden md:flex items-center justify-center flex-shrink-0">
              <RocketAnimation />
            </div>
          </div>
        </div>
        <div className="landing-hero-glow" />
      </section>

      {/* ─── STATS BAR ─── */}
      <section className="border-b border-slate-800/60 bg-sl-bg">
        <div className="container mx-auto px-6 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              ["40+", "API Endpoints"],
              ["10+", "Data Connectors"],
              ["15+", "Shape Tools"],
              ["\u221e", "Personalized Variations"],
            ].map(([val, label]) => (
              <div key={label}>
                <div className="stat-value text-3xl md:text-4xl font-bold">
                  {val}
                </div>
                <div className="text-sm text-slate-400 mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="landing-badge mb-4 inline-block">Features</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Everything You Need to Scale
            </h2>
            <p className="text-slate-400 text-lg">
              From design to delivery, SnapLayer handles every step of
              personalized content creation and automation.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {FEATURES.map((f) => (
              <div key={f.title} className="feature-card rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-sl-primary/15 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-3">
                  {f.desc}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {f.highlights.map((h) => (
                    <span
                      key={h}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-300 border border-slate-600/40"
                    >
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── AI INTEGRATIONS ─── */}
      <section className="landing-section-alt py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="landing-badge mb-4 inline-block">AI-Powered</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Built-In AI Tools
            </h2>
            <p className="text-slate-400 text-lg">
              Generate images and enhance copy right inside the editor.
              Bring your own API keys &mdash; no markup, no middleman.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
            {AI_FEATURES.map((f) => (
              <div key={f.title} className="feature-card rounded-xl p-6">
                <div className="ai-ring w-12 h-12 flex items-center justify-center mb-4">
                  <f.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">
                  {f.desc}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {f.providers.map((p) => (
                    <span
                      key={p}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/20"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PSD IMPORT / EXPORT ─── */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="landing-badge mb-4 inline-block">Professional Workflows</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                PSD Import &amp; Export
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                Import Adobe Photoshop files directly into SnapLayer &mdash;
                layers, text, effects, and blending modes are preserved. Design
                in Photoshop, personalize in SnapLayer, export back to PSD or
                render as images and PDFs.
              </p>
              <div className="space-y-3">
                {[
                  { icon: Upload, text: "Import .PSD preserving all layers and text" },
                  { icon: Layers, text: "Full layer management with drag-and-drop reorder" },
                  { icon: Download, text: "Export back to PSD or render to PNG/PDF" },
                  { icon: Zap, text: "Add dynamic variables like {{name}} to imported text" },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-sm text-slate-300">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* PSD layer stack illustration */}
            <div className="psd-stack flex flex-col items-center gap-3">
              {[
                { label: "Text Layer: {{company}}", color: "purple" },
                { label: "Logo Layer: brand-mark.png", color: "blue" },
                { label: "Background: gradient-fill", color: "slate" },
              ].map(({ label, color }, i) => (
                <div
                  key={i}
                  className={`psd-layer w-full max-w-sm px-5 py-4 flex items-center gap-3`}
                >
                  <div className={`w-6 h-6 rounded bg-${color}-500/20 border border-${color}-500/30 flex items-center justify-center`}>
                    <span className="text-[10px] font-bold text-slate-300">{i + 1}</span>
                  </div>
                  <span className="text-sm text-slate-300 font-mono">{label}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                <FileImage className="w-3.5 h-3.5" />
                design-template.psd &mdash; 3 layers imported
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── COMMUNITY & TEMPLATE SHARING ─── */}
      <section className="landing-section-alt py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="landing-badge mb-4 inline-block">Community</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Share, Discover &amp; Upvote Templates
            </h2>
            <p className="text-slate-400 text-lg">
              Browse the community gallery. Share your best designs. Upvote
              templates that inspire you. Three visibility levels give you full
              control.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3 max-w-4xl mx-auto">
            {[
              {
                icon: Eye,
                title: "My Templates",
                desc: "Private to your account. Design and iterate without anyone else seeing your work.",
                badge: "Private",
                badgeColor: "slate",
              },
              {
                icon: Star,
                title: "SnapLayer Official",
                desc: "Curated starter templates from the SnapLayer team. Report layouts, outreach cards, and more.",
                badge: "Curated",
                badgeColor: "purple",
              },
              {
                icon: Users,
                title: "Community Gallery",
                desc: "Templates shared by other users. Browse, clone, and upvote the best designs.",
                badge: "Public",
                badgeColor: "blue",
              },
            ].map((tab) => (
              <div key={tab.title} className="feature-card rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-sl-primary/10 flex items-center justify-center mx-auto mb-4">
                  <tab.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{tab.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-3">
                  {tab.desc}
                </p>
                <span className={`text-[10px] px-2.5 py-0.5 rounded-full bg-${tab.badgeColor}-500/10 text-${tab.badgeColor}-300 border border-${tab.badgeColor}-500/20`}>
                  {tab.badge}
                </span>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-8 gap-6">
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <ThumbsUp className="w-4 h-4 text-purple-400" />
              Upvote your favorites
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Share2 className="w-4 h-4 text-blue-400" />
              One-click sharing
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Download className="w-4 h-4 text-emerald-400" />
              Clone &amp; customize
            </div>
          </div>
        </div>
      </section>

      {/* ─── HOW AGENCIES USE SNAPLAYER ─── */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="landing-badge mb-4 inline-block">Workflow</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How Agencies Use SnapLayer
            </h2>
            <p className="text-slate-400 text-lg">
              From first outreach to signed contract &mdash; a complete
              pipeline for agencies that want to scale without hiring.
            </p>
          </div>

          <div className="max-w-3xl mx-auto relative">
            <div className="workflow-line hidden md:block" />

            <div className="space-y-8">
              {WORKFLOW_STEPS.map((s, i) => (
                <div key={i} className="relative flex gap-5 items-start">
                  <div className="hidden md:flex flex-col items-center gap-1 pt-1">
                    <div className="workflow-dot" />
                  </div>
                  <div className="feature-card rounded-xl p-5 flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-sl-primary/15 flex items-center justify-center md:hidden">
                        <s.icon className="w-4 h-4 text-purple-400" />
                      </div>
                      <span className="text-xs text-purple-400 font-semibold uppercase tracking-wide">
                        Step {i + 1}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold mb-1.5">
                      {s.title}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── INTERACTIVE DEMO ─── */}
      <section id="demo" className="py-20 md:py-28 scroll-mt-8">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <span className="landing-badge mb-4 inline-block">Live Demo</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              See It In Action
            </h2>
            <p className="text-slate-400 text-lg">
              Walk through SnapLayer&rsquo;s core workflows step by step —
              from data import to personalized delivery.
            </p>
          </div>

          <LandingDemo />
        </div>
      </section>

      {/* ─── USE CASES ─── */}
      <section className="landing-section-alt py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="landing-badge mb-4 inline-block">Use Cases</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              One Platform, Endless Use Cases
            </h2>
            <p className="text-slate-400 text-lg">
              Where other image personalization tools stop at static images,
              SnapLayer goes further &mdash; reports, documents,
              contracts, and data connectors built in.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {USE_CASES.map((uc) => (
              <div key={uc.title} className="usecase-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-sl-primary/15 flex items-center justify-center flex-shrink-0">
                    <uc.icon className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="text-base font-semibold">{uc.title}</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mb-3">
                  {uc.desc}
                </p>

                <div className="flex flex-wrap gap-1.5">
                  {uc.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-300 border border-slate-600/40"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ORIGIN STORY ─── */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <span className="landing-badge mb-4 inline-block">Our Story</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Built Because Nothing Else Existed
            </h2>
            <div className="origin-line mx-auto mb-8" />
            <div className="space-y-5 text-slate-300 text-lg leading-relaxed text-left md:text-center">
              <p>
                SnapLayer was born out of necessity. We were building{" "}
                <a href="https://embrfyr.app" target="_blank" rel="noreferrer" className="text-purple-400 hover:text-purple-300 underline underline-offset-2">
                  EmbrFyr
                </a>
                , a customer experience platform for agencies, and needed a way
                to generate personalized images, data-driven reports, and
                signable contracts &mdash; all at scale, all from one place.
              </p>
              <p>
                We looked at existing tools in the image personalization space.
                Some could personalize images in emails. Others could generate
                social graphics at scale. But none of them could create branded
                PDF reports from live data, route contracts for multi-party
                signing, or connect to data sources with 11+ keyword and
                backlink APIs.
              </p>
              <p>
                So we built it. SnapLayer became the engine that powers
                EmbrFyr &mdash; and now it&rsquo;s available as a standalone
                platform for any agency that needs the same capabilities. One
                platform that does what used to take three or four separate
                tools.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Heart className="w-4 h-4 text-red-400" />
                Built with purpose
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Rocket className="w-4 h-4 text-purple-400" />
                Powered by EmbrFyr
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                Actively shipping features
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SHIPPED + ROADMAP ─── */}
      <section className="landing-section-alt py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="landing-badge mb-4 inline-block">Progress</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Shipped &amp; What&rsquo;s Next
            </h2>
            <p className="text-slate-400 text-lg">
              SnapLayer is actively developed with new features shipping every
              month. Here&rsquo;s what&rsquo;s live and what&rsquo;s on the roadmap.
            </p>
          </div>

          {/* Shipped Features */}
          <div className="max-w-5xl mx-auto mb-10">
            <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wide mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Shipped &amp; Live
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {SHIPPED_FEATURES.map((item) => (
                <div key={item.title} className="roadmap-card shipped">
                  <div className="flex items-center gap-3 mb-2">
                    <item.icon className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <h3 className="text-sm font-semibold text-white">{item.title}</h3>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed mb-2">
                    {item.desc}
                  </p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                    Live
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming */}
          <div className="max-w-5xl mx-auto">
            <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-4 flex items-center gap-2">
              <Rocket className="w-4 h-4" /> Coming Soon
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {ROADMAP_ITEMS.map((item) => (
                <div key={item.title} className="roadmap-card">
                  <div className="flex items-center gap-3 mb-2">
                    <item.icon className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <h3 className="text-sm font-semibold text-white">{item.title}</h3>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed mb-2">
                    {item.desc}
                  </p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    item.status === "coming-soon"
                      ? "bg-purple-500/10 text-purple-300 border border-purple-500/20"
                      : "bg-blue-500/10 text-blue-300 border border-blue-500/20"
                  }`}>
                    {item.status === "coming-soon" ? "Coming Soon" : item.status.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section id="pricing" className="py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <span className="landing-badge mb-4 inline-block">Pricing</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-slate-400 text-lg mb-6">
              Choose the plan that fits your agency. All plans include the
              full editor, SnapSheet, API access, and data connectors.
            </p>

            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-3">
              <span className={`text-sm ${!annualBilling ? "text-white" : "text-slate-500"}`}>Monthly</span>
              <button
                onClick={() => setAnnualBilling(!annualBilling)}
                className={`relative w-12 h-6 rounded-full transition-colors ${annualBilling ? "bg-sl-primary" : "bg-slate-700"}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${annualBilling ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
              <span className={`text-sm ${annualBilling ? "text-white" : "text-slate-500"}`}>
                Annual <span className="text-emerald-400 text-xs font-semibold ml-1">Save 20%</span>
              </span>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
            {PRICING_PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`pricing-card ${plan.highlight ? "pricing-card-highlight" : ""}`}
              >
                <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                <p className="text-xs text-slate-500 mb-4">{plan.desc}</p>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-4xl font-extrabold">
                    ${annualBilling ? plan.annual : plan.price}
                  </span>
                  <span className="text-sm text-slate-500">/mo</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://getsnaplayer.com"
                  target="_blank"
                  rel="noreferrer"
                  className={`block text-center py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    plan.highlight
                      ? "bg-gradient-to-r from-sl-primary to-sl-accent text-white hover:opacity-90"
                      : "border border-slate-600 text-slate-300 hover:border-slate-400"
                  }`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-600 mt-6">
            <CreditCard className="w-3.5 h-3.5 inline mr-1" />
            Lifetime deals also available at{" "}
            <a href="https://getsnaplayer.com" target="_blank" rel="noreferrer" className="text-purple-400 hover:text-purple-300 underline">
              getsnaplayer.com
            </a>
          </p>
        </div>
      </section>

      {/* ─── INTEGRATIONS ─── */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="landing-badge mb-4 inline-block">
              Integrations
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Connected to Your Stack
            </h2>
            <p className="text-slate-400 text-lg">
              Pull live data from industry-leading platforms. More connectors
              ship every month.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 max-w-4xl mx-auto">
            {CONNECTORS.map((c) => (
              <div key={c.name} className="integration-card text-center">
                <Database className="w-5 h-5 mx-auto mb-2 text-blue-400" />
                <div className="text-xs font-medium text-slate-200 mb-0.5">
                  {c.name}
                </div>
                <div className="text-[10px] text-slate-500 mb-1.5">
                  {c.desc}
                </div>
                {c.available ? (
                  <span className="text-[10px] text-emerald-400">
                    Available
                  </span>
                ) : (
                  <span className="text-[10px] badge-coming-soon px-1.5 py-0.5 rounded-full">
                    Coming Soon
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── API ─── */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="landing-badge mb-4 inline-block">Developer API</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Build With the API
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                40+ endpoints for rendering images, generating PDFs, managing
                templates, triggering data connectors, and automating delivery.
                Integrate SnapLayer into any workflow with simple REST calls.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  "render_blob",
                  "render_base64",
                  "pdf_generate",
                  "reports_generate",
                  "documents_create",
                  "template_variables",
                ].map((ep) => (
                  <span
                    key={ep}
                    className="text-xs font-mono px-2.5 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300"
                  >
                    {ep}
                  </span>
                ))}
              </div>
            </div>

            <div className="code-block p-5 text-slate-300">
              <pre className="whitespace-pre-wrap">
                <span className="text-purple-400">const</span> response ={" "}
                <span className="text-purple-400">await</span>{" "}
                <span className="text-blue-400">fetch</span>(
                {"\n"}
                {"  "}<span className="text-emerald-400">{`'https://api.snaplayer.app/render_blob'`}</span>,
                {"\n"}
                {"  "}&#123;{"\n"}
                {"    "}method: <span className="text-emerald-400">'POST'</span>,{"\n"}
                {"    "}headers: &#123;{"\n"}
                {"      "}<span className="text-emerald-400">'Authorization'</span>:{" "}
                <span className="text-emerald-400">`Bearer $&#123;apiKey&#125;`</span>,{"\n"}
                {"      "}<span className="text-emerald-400">'Content-Type'</span>:{" "}
                <span className="text-emerald-400">'application/json'</span>{"\n"}
                {"    "}&#125;,{"\n"}
                {"    "}body: JSON.<span className="text-blue-400">stringify</span>(&#123;{"\n"}
                {"      "}template_id: <span className="text-emerald-400">'tmpl_abc123'</span>,{"\n"}
                {"      "}variables: &#123;{"\n"}
                {"        "}first_name: <span className="text-emerald-400">'Sarah'</span>,{"\n"}
                {"        "}company: <span className="text-emerald-400">'Acme Corp'</span>{"\n"}
                {"      "}&#125;{"\n"}
                {"    "}&#125;){"\n"}
                {"  "}&#125;{"\n"}
                );
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA + QUICK LINKS ─── */}
      <section className="landing-footer py-20 md:py-28 border-t border-slate-800/60">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Scale Your Agency?
            </h2>
            <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
              Start building personalized campaigns, branded reports, and
              automated contract workflows today.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg font-semibold text-white bg-gradient-to-r from-sl-primary to-sl-accent hover:opacity-90 transition-opacity"
              >
                <LogIn className="w-5 h-5" /> Log In to SnapLayer
              </Link>
              <Link
                to="/activation"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg font-semibold text-slate-200 border border-slate-600 hover:border-slate-400 transition-colors"
              >
                <KeyRound className="w-5 h-5" /> Activate License
              </Link>
            </div>

            {/* Quick links row */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
              <a
                href="https://getsnaplayer.com"
                target="_blank"
                rel="noreferrer"
                className="hover:text-white transition-colors flex items-center gap-1.5"
              >
                <ExternalLink className="w-3.5 h-3.5" /> LTD Sale
              </a>
              <a
                href="https://getsnaplayer.com/share"
                target="_blank"
                rel="noreferrer"
                className="hover:text-white transition-colors flex items-center gap-1.5"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Earn Extra Stacks
              </a>
              <a
                href="mailto:support@getsnaplayer.com"
                className="hover:text-white transition-colors flex items-center gap-1.5"
              >
                <Mail className="w-3.5 h-3.5" /> Support
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-16 pt-8 border-t border-slate-800/40 text-center text-xs text-slate-600">
            &copy; {new Date().getFullYear()} SnapLayer. All rights reserved.
          </div>
        </div>
      </section>
    </main>
  );
};

export default LandingPage;
